import { useMemo, useState } from "react";
import AdminStatePanel from "../../layout/AdminStatePanel";
import { useAdminWeatherSourceMetrics } from "../../../../../hooks/useAdminWeatherSourceMetrics";
import { formatDateTime } from "../../users/adminUserFormatters";
import { formatFrameAge } from "../../../../../utils/radarHealthAdminUtils";
import WeatherSiataObservabilityPanel from "./radar/WeatherSiataObservabilityPanel";

const RANGE_OPTIONS = [
  { value: "24h", label: "24 hours" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
];

function formatPercent(value) {
  if (value == null) {
    return "—";
  }
  return `${value}%`;
}

const WeatherSourceObservabilitySection = ({ canManage = false, onToast }) => {
  const [range, setRange] = useState("24h");
  const { summary, fallbacks, loading, refreshing, errorMessage, lastRefreshedAt, refresh, runCollection } =
    useAdminWeatherSourceMetrics({ range });

  const siataSummary = useMemo(
    () => summary?.sources?.find((row) => row.sourceCode === "SIATA_SANTA_ELENA") || null,
    [summary]
  );

  const combinedSummary = useMemo(
    () => summary?.sources?.find((row) => row.sourceCode === "RADAR_COMBINED") || null,
    [summary]
  );

  const notify = (tone, message) => onToast?.({ tone, message });

  const handleCollect = async () => {
    try {
      await runCollection();
      notify("success", "Metrics snapshot collected.");
    } catch (error) {
      notify("error", error.message || "Metrics collection failed.");
    }
  };

  return (
    <section id="weather-observability" className="weather-observability-section">
      <div className="weather-radar-health-section__header">
        <div>
          <h2 className="admin-section-title">Weather Source Observability</h2>
          <p className="weather-radar-health-section__description">
            Historical uptime, frame freshness, fallback frequency, sync failures, and rendered-source
            decisions. Admin only — not shown in resident UI.
          </p>
        </div>
        <div className="weather-observability-section__actions">
          <label>
            Range
            <select value={range} onChange={(event) => setRange(event.target.value)}>
              {RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={() => refresh({ silent: true })}
            disabled={loading || refreshing}
          >
            Refresh
          </button>
          {canManage ? (
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={handleCollect}
              disabled={loading || refreshing}
            >
              Collect snapshot
            </button>
          ) : null}
        </div>
      </div>

      {lastRefreshedAt ? (
        <p className="muted">Last refreshed {formatDateTime(lastRefreshedAt)}</p>
      ) : null}

      {loading && !summary ? (
        <AdminStatePanel variant="loading" message="Loading weather source metrics…" />
      ) : errorMessage ? (
        <AdminStatePanel
          variant="error"
          message={errorMessage}
          onRetry={() => refresh()}
        />
      ) : (
        <>
          {summary?.attentionItems?.length ? (
            <div className="weather-observability-attention admin-dash__card panel-card">
              <h3 className="admin-section-title">Needs attention</h3>
              <ul>
                {summary.attentionItems.map((item) => (
                  <li key={`${item.code}-${item.sourceCode}`} className={`attention--${item.severity}`}>
                    <strong>{item.sourceCode}</strong> — {item.message}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="weather-observability-cards admin-dash__summary-grid">
            <div className="admin-dash__card panel-card">
              <span className="weather-radar-calibration__label">Fallback events</span>
              <strong>{summary?.fallbackCount ?? 0}</strong>
            </div>
            <div className="admin-dash__card panel-card">
              <span className="weather-radar-calibration__label">Decision records</span>
              <strong>{summary?.decisionCount ?? 0}</strong>
            </div>
            <div className="admin-dash__card panel-card">
              <span className="weather-radar-calibration__label">Current rendered source</span>
              <strong>{combinedSummary?.metadata?.fallbackReason?.to || combinedSummary?.currentStatus || "—"}</strong>
            </div>
            <div className="admin-dash__card panel-card">
              <span className="weather-radar-calibration__label">Latest snapshot</span>
              <strong>{summary?.latestSnapshotAt ? formatDateTime(summary.latestSnapshotAt) : "—"}</strong>
            </div>
          </div>

          <div className="admin-dash__card admin-dash__card--wide panel-card">
            <h3 className="admin-section-title">Source reliability</h3>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Uptime</th>
                  <th>Avg frame age</th>
                  <th>Max frame age</th>
                  <th>Stale</th>
                  <th>Unavailable</th>
                  <th>Sync failures</th>
                  <th>Renderable</th>
                </tr>
              </thead>
              <tbody>
                {(summary?.sources || []).map((row) => (
                  <tr key={row.sourceCode}>
                    <td>{row.sourceLabel}</td>
                    <td>{row.currentStatus}</td>
                    <td>{formatPercent(row.uptimePercent)}</td>
                    <td>{formatFrameAge(row.averageFrameAgeMinutes)}</td>
                    <td>{formatFrameAge(row.maxFrameAgeMinutes)}</td>
                    <td>{row.staleCount}</td>
                    <td>{row.unavailableCount}</td>
                    <td>{row.syncFailureCount}</td>
                    <td>{row.isRenderable ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="admin-dash__card admin-dash__card--wide panel-card">
            <h3 className="admin-section-title">Fallback reasons</h3>
            {fallbacks?.fallbackReasonCounts &&
            Object.keys(fallbacks.fallbackReasonCounts).length > 0 ? (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Reason</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(fallbacks.fallbackReasonCounts).map(([reason, count]) => (
                    <tr key={reason}>
                      <td>{reason}</td>
                      <td>{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="muted">No fallback decision records in this range.</p>
            )}
          </div>

          <div className="admin-dash__card admin-dash__card--wide panel-card">
            <h3 className="admin-section-title">Rendered source distribution</h3>
            {fallbacks?.renderedSourceDistribution &&
            Object.keys(fallbacks.renderedSourceDistribution).length > 0 ? (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Rendered source</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(fallbacks.renderedSourceDistribution).map(([source, count]) => (
                    <tr key={source}>
                      <td>{source}</td>
                      <td>{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="muted">No rendered-source decision records in this range.</p>
            )}
          </div>

          <WeatherSiataObservabilityPanel siataSummary={siataSummary} fallbacks={fallbacks} />

          {summary?.topWarnings?.length ? (
            <div className="admin-dash__card panel-card">
              <h3 className="admin-section-title">Recent warnings</h3>
              <ul>
                {summary.topWarnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};

export default WeatherSourceObservabilitySection;
